import { useState, useEffect } from 'react';
import {
  IconSend,
  IconDownload,
  IconQrcode,
  IconRefresh,
  IconSearch,
  IconLoader2,
  IconSelector,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertAction } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

export function AssignEsimsPage() {
  // Tab state
  const [currentTab, setCurrentTab] = useState('assign');

  // Assignment tab state
  const [availableCount, setAvailableCount] = useState(0);
  const [distributors, setDistributors] = useState<any[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<any>(null);
  const [starterPacks, setStarterPacks] = useState<any[]>([]);
  const [selectedPack, setSelectedPack] = useState<any>(null);
  const [selectedSimType, setSelectedSimType] = useState<number | ''>('');
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assignedSims, setAssignedSims] = useState<any[]>([]);
  const [assignmentDetails, setAssignmentDetails] = useState<any>(null);

  // Combobox open states
  const [distributorOpen, setDistributorOpen] = useState(false);
  const [packOpen, setPackOpen] = useState(false);

  // QR Code tab state
  const [qrStats, setQrStats] = useState({ missingQrCodes: 0, withQrCodes: 0, total: 0 });
  const [generatingQr, setGeneratingQr] = useState(false);
  const [qrProgress, setQrProgress] = useState(0);
  const [qrError, setQrError] = useState('');
  const [qrSuccess, setQrSuccess] = useState('');
  const [qrResults, setQrResults] = useState<any[]>([]);

  // Search SIM tab state
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResult, setSearchResult] = useState<any>(null);
  const [regeneratingQr, setRegeneratingQr] = useState(false);

  const esimTypes = [
    { value: 12, label: 'eSIM - Portal Distribution ' },
    { value: 13, label: 'eSIM - Online Orders' },
    { value: 14, label: 'eSIM - Physical Distribution' },
    { value: 36, label: 'eSIM - Allocated Inventory' },
  ];

  useEffect(() => {
    loadAvailableCount();
    loadDistributors();
    loadQrStats();
  }, []);

  const loadAvailableCount = async () => {
    try {
      const res = await api.get('/sim-cards/assign-esims/available-count');
      setAvailableCount(res.data.count);
    } catch (err) {
      console.error('Failed to load available count', err);
    }
  };

  const loadDistributors = async () => {
    try {
      const res = await api.get('/sim-cards/assign-esims/distributors');
      setDistributors(res.data.data || []);
    } catch (err) {
      console.error('Failed to load distributors', err);
    }
  };

  const loadQrStats = async () => {
    try {
      const res = await api.get('/sim-cards/assign-esims/qr-code-stats');
      setQrStats(res.data);
    } catch (err) {
      console.error('Failed to load QR stats', err);
    }
  };

  const handleDistributorChange = async (newValue: any) => {
    setSelectedDistributor(newValue);
    setStarterPacks([]);
    setSelectedPack(null);
    setSelectedSimType('');

    if (!newValue) return;

    try {
      const res = await api.get(`/sim-cards/assign-esims/starter-packs?distributorId=${newValue.i_customer}`);
      setStarterPacks(res.data.data || []);
    } catch (err) {
      console.error('Failed to load starter packs', err);
    }
  };

  const handleDownloadCSV = () => {
    if (!assignedSims.length || !assignmentDetails) return;

    const headers = [
      'ICCID',
      'IMSI',
      'Distributor Name',
      'Distributor ID',
      'Activation Package',
      'SIM Type',
      'Assigned Date',
      'Assigned By',
    ];

    const escapeCSV = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const rows = assignedSims.map((sim) => [
      sim.iccid || '',
      sim.imsi || '',
      assignmentDetails.distributorName || '',
      assignmentDetails.distributorId || '',
      assignmentDetails.activationName || '',
      assignmentDetails.simTypeName || '',
      sim.assigned_time ? new Date(sim.assigned_time).toLocaleString() : '',
      sim.scanned_out_by || '',
    ]);

    const csvRows = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map((cell) => escapeCSV(String(cell))).join(',')),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.download = `sim-assignment-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setAssignedSims([]);
    setAssignmentDetails(null);
    setLoading(true);

    try {
      const quantityNum = parseInt(quantity);
      if (quantityNum > availableCount) {
        throw new Error(`Only ${availableCount} eSIMs available`);
      }

      if (!selectedSimType) {
        throw new Error('Please select an eSIM type');
      }

      const res = await api.post('/sim-cards/assign-esims/assign', {
        distributorId: selectedDistributor.i_customer,
        quantity: quantityNum,
        activationId: selectedPack.id,
        simType: selectedSimType,
      });

      setSuccess(`Successfully assigned ${res.data.assignedCount} eSIMs!`);
      setAssignedSims(res.data.assignedSims || []);
      setAssignmentDetails(res.data.assignmentDetails || null);
      setQuantity('');
      setSelectedDistributor(null);
      setSelectedPack(null);
      setSelectedSimType('');
      setStarterPacks([]);
      loadAvailableCount();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to assign eSIMs');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQrCodes = async () => {
    setGeneratingQr(true);
    setQrError('');
    setQrSuccess('');
    setQrResults([]);
    setQrProgress(0);

    try {
      const batchSize = 50;
      let totalProcessed = 0;
      let totalFailed = 0;
      const allResults: any[] = [];

      while (true) {
        const res = await api.post('/sim-cards/assign-esims/generate-qr-codes', {
          batchSize,
        });

        if (res.data.processed === 0) {
          break;
        }

        totalProcessed += res.data.processed;
        totalFailed += res.data.failed;
        allResults.push(...res.data.results);

        await loadQrStats();
        const percentage = Math.round(
          ((qrStats.withQrCodes + totalProcessed) / qrStats.total) * 100
        );
        setQrProgress(percentage);

        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      setQrSuccess(
        `Successfully generated ${totalProcessed} QR codes! ${totalFailed > 0 ? `${totalFailed} failed.` : ''}`
      );
      setQrResults(allResults);
      await loadQrStats();
    } catch (err: any) {
      setQrError(err.response?.data?.message || err.message || 'Failed to generate QR codes');
    } finally {
      setGeneratingQr(false);
      setQrProgress(0);
    }
  };

  const handleSearchSim = async () => {
    if (!searchIdentifier.trim()) {
      setSearchError('Please enter an ICCID or IMSI');
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);

    try {
      const res = await api.get(
        `/sim-cards/assign-esims/sim-details?identifier=${encodeURIComponent(searchIdentifier)}`
      );
      setSearchResult(res.data.data);
    } catch (err: any) {
      setSearchError(err.response?.data?.message || err.message || 'SIM card not found');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleRegenerateQrCode = async () => {
    if (!searchResult) return;

    setRegeneratingQr(true);
    setSearchError('');

    try {
      const res = await api.post('/sim-cards/assign-esims/regenerate-qr-code', {
        identifier: searchResult.iccid,
      });

      setSearchResult({
        ...searchResult,
        qr_code_image: res.data.qrCodeUrl,
      });

      setSearchError('');
      alert('QR code regenerated successfully!');
    } catch (err: any) {
      setSearchError(err.response?.data?.message || err.message || 'Failed to regenerate QR code');
    } finally {
      setRegeneratingQr(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Assign eSIMs"
        subtitle="Assign eSIMs to distributors with starter pack activation"
      />

      <Tabs value={currentTab} onValueChange={(val) => setCurrentTab(val as string)}>
        {/* Tab Navigation */}
        <MainCard className="mb-6">
          <TabsList variant="line">
            <TabsTrigger value="assign">
              <IconSend className="size-4" />
              Assign eSIMs
            </TabsTrigger>
            <TabsTrigger value="qr">
              <IconQrcode className="size-4" />
              Generate QR Codes
            </TabsTrigger>
            <TabsTrigger value="search">
              <IconSearch className="size-4" />
              Search SIM
            </TabsTrigger>
          </TabsList>
        </MainCard>

        {/* Tab 0: Assign eSIMs */}
        <TabsContent value="assign">
          {/* Available Count Card */}
          <div className="mb-6 rounded-lg border border-border/60 bg-card p-5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Available eSIMs</p>
            <p className="text-3xl font-semibold tabular-nums mt-1">{availableCount.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Ready for assignment</p>
          </div>

          {/* Assignment Form */}
          <MainCard>
            <form onSubmit={handleAssign}>
              <div className="flex flex-col gap-6">
                {/* Distributor Selection */}
                <div className="space-y-2">
                  <Label>Select Distributor *</Label>
                  <Popover open={distributorOpen} onOpenChange={setDistributorOpen}>
                    <PopoverTrigger
                      type="button"
                      role="combobox"
                      aria-expanded={distributorOpen}
                      className={cn(
                        buttonVariants({ variant: 'outline', size: 'default' }),
                        'w-full justify-between h-8 font-normal'
                      )}
                    >
                      <span
                        className={cn(
                          'truncate',
                          !selectedDistributor && 'text-muted-foreground'
                        )}
                      >
                        {selectedDistributor
                          ? `${selectedDistributor.company_name} (ID: ${selectedDistributor.i_customer})`
                          : 'Select Distributor...'}
                      </span>
                      <IconSelector className="size-4 shrink-0 opacity-50" />
                    </PopoverTrigger>
                    <PopoverContent className="w-(--anchor-width) p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search distributor..." />
                        <CommandList>
                          <CommandEmpty>No distributor found.</CommandEmpty>
                          <CommandGroup>
                            {distributors.map((dist) => (
                              <CommandItem
                                key={dist.i_customer}
                                value={`${dist.company_name} ${dist.i_customer}`}
                                data-checked={
                                  selectedDistributor?.i_customer === dist.i_customer
                                }
                                onSelect={() => {
                                  handleDistributorChange(dist);
                                  setDistributorOpen(false);
                                }}
                              >
                                <div>
                                  <p className="text-sm">{dist.company_name}</p>
                                  <span className="text-xs text-muted-foreground">
                                    ID: {dist.i_customer}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    Choose the distributor to assign eSIMs to
                  </p>
                </div>

                {/* Starter Pack Selection */}
                {selectedDistributor && (
                  <div className="space-y-2">
                    <Label>Select Starter Pack *</Label>
                    <Popover open={packOpen} onOpenChange={setPackOpen}>
                      <PopoverTrigger
                        type="button"
                        role="combobox"
                        aria-expanded={packOpen}
                        disabled={starterPacks.length === 0}
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'default' }),
                          'w-full justify-between h-8 font-normal'
                        )}
                      >
                        <span
                          className={cn(
                            'truncate',
                            !selectedPack && 'text-muted-foreground'
                          )}
                        >
                          {selectedPack
                            ? selectedPack.name || `Pack ${selectedPack.id}`
                            : 'Select Starter Pack...'}
                        </span>
                        <IconSelector className="size-4 shrink-0 opacity-50" />
                      </PopoverTrigger>
                      <PopoverContent className="w-(--anchor-width) p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search starter pack..." />
                          <CommandList>
                            <CommandEmpty>
                              No starter packs available for this distributor
                            </CommandEmpty>
                            <CommandGroup>
                              {starterPacks.map((pack) => (
                                <CommandItem
                                  key={pack.id}
                                  value={pack.name || `Pack ${pack.id}`}
                                  data-checked={selectedPack?.id === pack.id}
                                  onSelect={() => {
                                    setSelectedPack(pack);
                                    setPackOpen(false);
                                  }}
                                >
                                  {pack.name || `Pack ${pack.id}`}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      Choose the activation package
                    </p>
                  </div>
                )}

                {/* eSIM Type Selection */}
                {selectedPack && (
                  <div className="space-y-2">
                    <Label>eSIM Type *</Label>
                    <Select
                      value={selectedSimType === '' ? null : selectedSimType}
                      onValueChange={(val: any) => setSelectedSimType(val as number)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select eSIM Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {esimTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Select the type of eSIM to assign
                    </p>
                  </div>
                )}

                {/* Shopify Warning for Online Orders */}
                {selectedSimType === 13 && (
                  <Alert variant="warning">
                    <IconAlertTriangle className="size-4" />
                    <AlertDescription>
                      <strong>Reminder:</strong> Please update the stock quantity for these SIM
                      cards in Shopify as well.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Quantity Input */}
                {selectedSimType && (
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      required
                      min={1}
                      max={availableCount}
                      placeholder="Enter quantity"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum: {availableCount.toLocaleString()} eSIMs
                    </p>
                  </div>
                )}

                {/* Alerts */}
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                    <AlertAction>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setError('')}
                      >
                        <IconX className="size-3" />
                      </Button>
                    </AlertAction>
                  </Alert>
                )}

                {success && (
                  <Alert variant="success">
                    <AlertDescription>{success}</AlertDescription>
                    <AlertAction>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setSuccess('')}
                      >
                        <IconX className="size-3" />
                      </Button>
                    </AlertAction>
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  disabled={loading || !selectedPack || !selectedSimType || !quantity}
                  className="w-full py-3"
                >
                  {loading ? (
                    <IconLoader2 className="size-4 animate-spin" />
                  ) : (
                    <IconSend className="size-4" />
                  )}
                  {loading ? 'Assigning...' : 'Assign eSIMs'}
                </Button>
              </div>
            </form>
          </MainCard>

          {/* Info */}
          {selectedDistributor && (
            <Alert variant="info" className="mt-6">
              <IconInfoCircle className="size-4" />
              <AlertDescription>
                Assigning to: <strong>{selectedDistributor.company_name}</strong>
              </AlertDescription>
            </Alert>
          )}

          {/* Assignment Results */}
          {assignedSims.length > 0 && assignmentDetails && (
            <MainCard className="mt-8">
              <div className="flex justify-between items-center mb-6">
                <h5 className="text-lg font-bold">Assignment Complete</h5>
                <Button
                  variant="default"
                  onClick={handleDownloadCSV}
                >
                  <IconDownload className="size-4" />
                  Download CSV
                </Button>
              </div>

              <Separator className="mb-6" />

              {/* Assignment Summary */}
              <div className="mb-6">
                <h6 className="text-base font-semibold mb-4">Assignment Summary</h6>
                <div className="flex flex-col gap-2">
                  <p className="text-sm">
                    <strong>Distributor:</strong> {assignmentDetails.distributorName} (ID:{' '}
                    {assignmentDetails.distributorId})
                  </p>
                  <p className="text-sm">
                    <strong>Activation Package:</strong> {assignmentDetails.activationName}
                  </p>
                  <p className="text-sm">
                    <strong>SIM Type:</strong> {assignmentDetails.simTypeName}
                  </p>
                  <p className="text-sm">
                    <strong>Total Assigned:</strong> {assignedSims.length} eSIMs
                  </p>
                  <p className="text-sm">
                    <strong>Assigned By:</strong> {assignmentDetails.assignedBy}
                  </p>
                  <p className="text-sm">
                    <strong>Assigned At:</strong>{' '}
                    {new Date(assignmentDetails.assignedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              <Separator className="mb-6" />

              {/* Assigned SIM Cards Table */}
              <h6 className="text-base font-semibold mb-4">
                Assigned SIM Cards ({assignedSims.length})
              </h6>
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold">ICCID</TableHead>
                      <TableHead className="font-bold">IMSI</TableHead>
                      <TableHead className="font-bold">Assigned Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignedSims.map((sim, index) => (
                      <TableRow key={sim.iccid || index}>
                        <TableCell>{sim.iccid || 'N/A'}</TableCell>
                        <TableCell>{sim.imsi || 'N/A'}</TableCell>
                        <TableCell>
                          {sim.assigned_time
                            ? new Date(sim.assigned_time).toLocaleString()
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </MainCard>
          )}
        </TabsContent>

        {/* Tab 1: Generate QR Codes */}
        <TabsContent value="qr">
          {/* QR Code Statistics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-lg border border-border/60 bg-card p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="size-2 rounded-full bg-destructive" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Missing QR Codes</p>
              </div>
              <p className="text-2xl font-semibold tabular-nums">{qrStats.missingQrCodes.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">Need to be generated</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-card p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="size-2 rounded-full bg-success" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">With QR Codes</p>
              </div>
              <p className="text-2xl font-semibold tabular-nums">{qrStats.withQrCodes.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">Already generated</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-card p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="size-2 rounded-full bg-primary" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total eSIMs</p>
              </div>
              <p className="text-2xl font-semibold tabular-nums">{qrStats.total.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {qrStats.total > 0
                  ? `${Math.round((qrStats.withQrCodes / qrStats.total) * 100)}% complete`
                  : 'No eSIMs'}
              </p>
            </div>
          </div>

          {/* Generate Button */}
          <MainCard className="mb-6">
            <h6 className="text-base font-semibold mb-2">Generate QR Codes</h6>
            <p className="text-sm text-muted-foreground mb-6">
              This will generate QR codes for all eSIMs that don't have them yet. The process
              runs in batches and may take several minutes.
            </p>

            {generatingQr && (
              <div className="mb-6">
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${qrProgress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  Generating QR codes... {qrProgress}%
                </p>
              </div>
            )}

            {qrError && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{qrError}</AlertDescription>
                <AlertAction>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setQrError('')}
                  >
                    <IconX className="size-3" />
                  </Button>
                </AlertAction>
              </Alert>
            )}

            {qrSuccess && (
              <Alert variant="success" className="mb-6">
                <AlertDescription>{qrSuccess}</AlertDescription>
                <AlertAction>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setQrSuccess('')}
                  >
                    <IconX className="size-3" />
                  </Button>
                </AlertAction>
              </Alert>
            )}

            <div className="flex gap-4">
              <Button
                variant="default"
                size="lg"
                onClick={handleGenerateQrCodes}
                disabled={generatingQr || qrStats.missingQrCodes === 0}
              >
                {generatingQr ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconQrcode className="size-4" />
                )}
                {generatingQr ? 'Generating...' : 'Generate QR Codes'}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={loadQrStats}
                disabled={generatingQr}
              >
                <IconRefresh className="size-4" />
                Refresh Stats
              </Button>
            </div>
          </MainCard>

          {/* Results Table */}
          {qrResults.length > 0 && (
            <MainCard>
              <h6 className="text-base font-semibold mb-4">
                Generation Results ({qrResults.length})
              </h6>
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold">ICCID</TableHead>
                      <TableHead className="font-bold">Status</TableHead>
                      <TableHead className="font-bold">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qrResults.map((result, index) => (
                      <TableRow key={result.iccid || index}>
                        <TableCell>{result.iccid || 'N/A'}</TableCell>
                        <TableCell>
                          {result.success ? (
                            <Badge className="bg-green-600 text-white">Success</Badge>
                          ) : (
                            <Badge variant="destructive">Failed</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.success ? (
                            <span className="text-xs text-muted-foreground">
                              {result.qrCodeUrl}
                            </span>
                          ) : (
                            <span className="text-xs text-destructive">{result.error}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </MainCard>
          )}
        </TabsContent>

        {/* Tab 2: Search SIM */}
        <TabsContent value="search">
          <MainCard className="mb-6">
            <h6 className="text-base font-semibold mb-2">Search SIM Card</h6>
            <p className="text-sm text-muted-foreground mb-6">
              Enter an ICCID or IMSI to view SIM card details and QR code
            </p>

            <div className="flex gap-4 mb-6">
              <Input
                value={searchIdentifier}
                onChange={(e) => setSearchIdentifier(e.target.value)}
                placeholder="Enter ICCID or IMSI"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSim();
                  }
                }}
              />
              <Button
                variant="default"
                size="lg"
                onClick={handleSearchSim}
                disabled={searchLoading}
                className="min-w-[150px]"
              >
                {searchLoading ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconSearch className="size-4" />
                )}
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {searchError && (
              <Alert variant="destructive">
                <AlertDescription>{searchError}</AlertDescription>
                <AlertAction>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setSearchError('')}
                  >
                    <IconX className="size-3" />
                  </Button>
                </AlertAction>
              </Alert>
            )}
          </MainCard>

          {/* Search Results */}
          {searchResult && (
            <MainCard>
              <h6 className="text-base font-semibold mb-4">SIM Card Details</h6>
              <Separator className="mb-6" />

              <div className="flex flex-wrap gap-6">
                <div className="flex-1 basis-[400px] min-w-0">
                  <div className="flex flex-col gap-4">
                    <div>
                      <span className="text-xs text-muted-foreground">ICCID</span>
                      <p className="text-sm font-bold">{searchResult.iccid || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">IMSI</span>
                      <p className="text-sm font-bold">{searchResult.imsi || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">SIM Type</span>
                      <p className="text-sm font-bold">
                        {searchResult.sim_type || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">QR Code Data</span>
                      <p className="break-all font-mono text-sm bg-muted p-2 rounded-md mt-1">
                        {searchResult.qr_code || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 basis-[400px] min-w-0 text-center">
                  <span className="text-xs text-muted-foreground">QR Code Image</span>
                  {searchResult.qr_code_image ? (
                    <>
                      <img
                        src={searchResult.qr_code_image}
                        alt="QR Code"
                        className="max-w-full h-auto border border-border rounded-lg mt-2"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateQrCode}
                        disabled={regeneratingQr}
                        className="mt-4"
                      >
                        {regeneratingQr ? (
                          <IconLoader2 className="size-4 animate-spin" />
                        ) : (
                          <IconRefresh className="size-4" />
                        )}
                        {regeneratingQr ? 'Regenerating...' : 'Regenerate QR Code'}
                      </Button>
                    </>
                  ) : (
                    <div className="mt-4">
                      <Alert variant="warning">
                        <IconAlertTriangle className="size-4" />
                        <AlertDescription>No QR code image available</AlertDescription>
                      </Alert>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleRegenerateQrCode}
                        disabled={regeneratingQr || !searchResult.qr_code}
                        className="mt-4"
                      >
                        {regeneratingQr ? (
                          <IconLoader2 className="size-4 animate-spin" />
                        ) : (
                          <IconQrcode className="size-4" />
                        )}
                        {regeneratingQr ? 'Generating...' : 'Generate QR Code'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </MainCard>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
